import React from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";

interface Company {
  name: string;
  logo: string;
  description: string;
  industry: string;
}

const TrustedBySection: React.FC = () => {
  const trustedCompanies: Company[] = [
    {
      name: "Stellantis",
      logo: "/images/stellantis.svg",
      description: "Fortune 500 Automotive Leader",
      industry: "Automotive"
    },
    {
      name: "RTE",
      logo: "/images/RTE_logo.png", 
      description: "European Energy Grid Operator",
      industry: "Energy"
    }
  ];

  return (
    <div className="relative pt-16 pb-20 bg-gradient-to-br from-amber-500/3 via-blue-500/5 to-purple-500/3">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl" />
      </div>

      <div className="text-center mb-16 relative z-10">
        <motion.div
          className="relative inline-block mb-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight">
            Trusted by
            <br />
            <span className="bg-gradient-to-r from-amber-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Industry Leaders
            </span>
          </h2>
        </motion.div>
        
        <motion.p 
          className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          Join Fortune 500 companies who trust ShredsAI for their development workflow
        </motion.p>
      </div>

      <div className="max-w-5xl mx-auto px-8 relative z-10">
        <motion.div 
          className="flex flex-col md:flex-row items-center justify-center gap-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          {trustedCompanies.map((company, index) => (
            <motion.div
              key={company.name}
              className="group text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="relative mb-6"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-blue-400/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <img
                    src={company.logo}
                    alt={company.name}
                    className={`w-64 h-32 object-contain mx-auto ${
                      company.name === 'RTE' 
                        ? 'filter-none' 
                        : 'filter brightness-0 invert'
                    }`}
                  />
                </div>
              </motion.div>

              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white">{company.name}</h3>
                <p className="text-gray-400 text-sm">{company.description}</p>
                <div className="inline-flex items-center space-x-2 px-3 py-1 bg-amber-400/15 rounded-full">
                  <Award className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-300 font-medium text-xs">{company.industry}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default TrustedBySection;